import React, { forwardRef, useContext, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton } from 'react-share';

import CloseIcon from '../../assets/icons/CloseIcon';
import FacebookIcon from '../../assets/icons/FacebookIcon';
import LinkedInIcon from '../../assets/icons/LinkedInIcon';
import MicrosoftTeamsIcon from '../../assets/icons/MicrosoftTeamsIcon';
import TwitterIcon from '../../assets/icons/TwitterIcon';
import { TenantCtx } from '../../containers/CommonLayout/CommonLayout';
import styled, { css } from '../../styled';
import { colorsPalette, SMALL_MARGIN_PX } from '../../theme';
import { TenantsRefs } from '../../utils/const';
import { getCourseSocialShareImage, getSocialShareImage, translate } from '../../utils/helpers';
import useShareTeams from '../../utils/hooks/useShareTeams';
import ImageWithLoader from '../ImageWithLoader/ImageWithLoader';
import { CardWrapper } from '../UI/CardWrapper';
import { ShareButton } from '../UI/ShareButton';

export enum ShareType {
  Pathway = 'pathway',
  Course = 'course',
}

type GenerateShareDataInput = {
  type: ShareType;
  resultHash?: string | null;
  tenantName?: string | null;
  entityName?: string | null;
  cpdCredits?: string | null;
  category?: string | null;
  fbName?: string | null;
  twitterHandle?: string | null;
};

const generateShareData = ({
  type,
  resultHash,
  tenantName,
  entityName,
  cpdCredits,
  category,
  fbName,
  twitterHandle,
}: GenerateShareDataInput) => {
  const defaultShareText =
    tenantName === TenantsRefs.Fu
      ? translate('page.pathway.pathwayShareFu')
      : tenantName === TenantsRefs.Su
      ? translate('page.pathway.pathwayShareSu')
      : undefined;

  switch (type) {
    case ShareType.Pathway: {
      const shareText =
        entityName && cpdCredits
          ? translate('page.test.shareText', { pathwayName: entityName, cpdCredits })
          : defaultShareText;
      const shareTwitterText =
        entityName && category
          ? translate('page.test.shareTwitterText', {
              pathwayName: entityName,
              twitterHandle,
              category,
            })
          : defaultShareText;
      const shareFacebookText =
        entityName && category
          ? translate('page.test.shareFacebookText', { pathwayName: entityName, fbName, category })
          : defaultShareText;

      return {
        shareLink: `${window?.location?.origin}/share/${resultHash}`,
        socialShareImgUrl: getSocialShareImage(resultHash, tenantName),
        shareText,
        shareTwitterText,
        shareFacebookText,
      };
    }
    case ShareType.Course: {
      const shareText = entityName
        ? translate('page.course.shareText', { courseName: entityName })
        : '';
      const shareTwitterText = entityName
        ? translate('page.course.shareTwitterText', {
            courseName: entityName,
            twitterHandle,
            category,
          })
        : '';
      const shareFacebookText = entityName
        ? translate('page.course.shareFacebookText', { courseName: entityName, fbName })
        : '';

      return {
        shareLink: `${window?.location?.origin}/share/courses/${resultHash}`,
        socialShareImgUrl: getCourseSocialShareImage(resultHash, tenantName),
        shareText,
        shareTwitterText,
        shareFacebookText,
      };
    }
  }
};

const formatHashTags = (hashtags?: (string | null)[] | null): string[] => {
  const arr = (hashtags ? hashtags.filter((hashtag) => Boolean(hashtag)) : []) as string[];

  return arr.map((hashtag) => (hashtag?.startsWith('#') ? hashtag?.substring(1) : hashtag));
};

interface ShareModalProps {
  closeModal?: () => void;
  resultHash?: string | null;
  entityName?: string | null;
  cpdCredits?: string | null;
  category?: string | null;
  type?: ShareType;
}

const ShareModal = forwardRef<HTMLDivElement, ShareModalProps>(
  ({ closeModal, resultHash, entityName, cpdCredits, category, type = ShareType.Pathway }, ref) => {
    const { t } = useTranslation();
    const { tenant } = useContext(TenantCtx);

    const tenantName = tenant?.name;
    const fbName = tenant?.fb_name;
    const twitterHandle = tenant?.twitter_handle;
    const twitterHashtags = tenant?.twitter_hashtags_array;

    const { handleTeamsShare, teamsShareRef } = useShareTeams();
    const formattedHashtags = useMemo(() => formatHashTags(twitterHashtags), [twitterHashtags]);

    const { shareLink, socialShareImgUrl, shareText, shareFacebookText, shareTwitterText } =
      useMemo(
        () =>
          generateShareData({
            type,
            resultHash,
            tenantName,
            cpdCredits,
            category,
            entityName,
            fbName,
            twitterHandle,
          }),
        [type, resultHash, tenantName, entityName, category, cpdCredits, fbName, twitterHandle],
      );

    return (
      <StyledCardWrapper ref={ref} data-testid="shareModal">
        <HeaderWrapper>
          <Title variant="h2">{t('page.test.shareYourSuccess')}</Title>
          <StyledCloseIcon
            data-testid="shareModalClose"
            color={colorsPalette.gray}
            onClick={closeModal}
          />
        </HeaderWrapper>
        <Content>
          <CertificateImgWrapper>
            <StyledImageWithLoader
              ratio={52}
              imgWrapperCss={certificateImgWrapperWithBg}
              path={socialShareImgUrl}
              alt={t('page.test.share')}
              srcset={[{ width: 750 }]}
              sizes="100vw"
              data-testid="certificateImg"
            />
          </CertificateImgWrapper>
          <ButtonsWrapper>
            <StyledLinkedinShareButton
              title={shareText}
              url={shareLink}
              resetButtonStyle={true}
              data-testid="linkedinShare"
            >
              <StyledShareButton>
                <LinkedInIcon color={colorsPalette.white} />
                {t('page.test.linkedIn')}
              </StyledShareButton>
            </StyledLinkedinShareButton>
            <StyledShareButton data-testid="shareMSTeams" onClick={handleTeamsShare}>
              <MicrosoftTeamsIcon color={colorsPalette.white} />
              {t('page.test.microsoftTeams')}
            </StyledShareButton>
            <FacebookShareButton
              data-testid="facebookShare"
              quote={shareFacebookText}
              url={shareLink}
              resetButtonStyle={true}
            >
              <StyledShareButton>
                <FacebookIcon color={colorsPalette.white} />
                {t('page.test.facebook')}
              </StyledShareButton>
            </FacebookShareButton>
            <StyledTwitterShareButton
              data-testid="twitterShare"
              title={shareTwitterText}
              url={shareLink}
              resetButtonStyle={true}
              hashtags={formattedHashtags}
            >
              <StyledShareButton>
                <TwitterIcon color={colorsPalette.white} />
                {t('page.test.twitter')}
              </StyledShareButton>
            </StyledTwitterShareButton>
            <TeamsShareOriginal
              className="teams-share-button"
              data-href={shareLink}
              data-msg-text={shareText}
              ref={teamsShareRef}
            />
          </ButtonsWrapper>
        </Content>
      </StyledCardWrapper>
    );
  },
);

export default ShareModal;

const certificateImgWrapperWithBg = css`
  border-radius: 5px;
`;
const StyledCardWrapper = styled(CardWrapper)`
  width: 100%;
  background: ${({ theme }) => theme.palette.secondary.light};
  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 900px;
  }
  &:focus {
    outline: none;
  }
`;
const HeaderWrapper = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${SMALL_MARGIN_PX};
`;
const Title = styled(Typography)`
  font-size: 24px;
  line-height: 1.2;
  font-weight: bold;
  margin-bottom: 0;
  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 28px;
  }
`;
const StyledCloseIcon = styled(CloseIcon)`
  width: 14px;
  height: 14px;
  cursor: pointer;
`;
const Content = styled(Box)`
  display: flex;
  flex-direction: column;
  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
  }
`;
const CertificateImgWrapper = styled('div')`
  flex: 1;
  position: relative;
  margin-bottom: ${SMALL_MARGIN_PX};
  display: flex;
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-right: ${SMALL_MARGIN_PX};
    margin-bottom: 0;
  }
`;
const StyledImageWithLoader = styled(ImageWithLoader)`
  border-radius: 5px;
  width: 100%;
`;
const ButtonsWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  & button,
  & a {
    margin-bottom: 6px;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;
const createStyledShareButtonWrapper = (tag) => styled(tag)`
  &:focus {
    outline: none;
  }
`;
const StyledLinkedinShareButton = createStyledShareButtonWrapper(LinkedinShareButton);
const StyledTwitterShareButton = createStyledShareButtonWrapper(TwitterShareButton);
const StyledShareButton = styled(ShareButton)`
  cursor: pointer;
  margin-bottom: 6px;
  &:last-child {
    margin-bottom: 0;
  }
`;
const TeamsShareOriginal = styled('div')`
  display: none;
`;
